import { saveBlob } from "@/lib/blob-store";
import { extFromFilename, mimeFromExt } from "@/lib/mime-ext";
import type { Workshop } from "@/content/workshops";
import type { Upload } from "@/stores/useAssetLocker";

/**
 * Turn a user-selected `File` into an AssetLocker {@link Upload},
 * persisting the bytes to the blob store along the way. The returned
 * entry is ready to hand straight to `useAssetLocker.add(workshopId,
 * upload)` — the store takes over IDB persistence + the (optional)
 * GitHub mirror from there. This is the ingest half of the R1
 * file-upload slice; everything downstream (chip render, lightbox,
 * blob self-heal, sync) already exists in `AssetLocker.tsx` + the
 * store.
 *
 * `mimeType` prefers the browser-provided `file.type`; when the browser
 * leaves it blank (common for uncommon extensions) we derive it from
 * the filename via the shared {@link mimeFromExt} table so the chip
 * still tags sensibly. We deliberately do NOT set `format` — that field
 * is reserved for seed entries with explicit shortcodes; user uploads
 * let `AssetLocker.formatTag()` derive the tag from `mimeType`.
 *
 * Blob-key scheme: `upload:<workshopId>:<uuid>` — unique per file (never
 * the filename, so re-uploading a same-named file can't collide) and
 * easy to spot alongside the `snap:` / `gh:` conventions in dev tooling.
 */
export async function fileToUpload(
  file: File,
  workshopId: Workshop["id"],
  addedBy: string,
): Promise<Upload> {
  const id = crypto.randomUUID();
  const blobKey = `upload:${workshopId}:${id}`;
  const mimeType = file.type || mimeFromExt(extFromFilename(file.name));
  await saveBlob(blobKey, file);
  return {
    id,
    label: file.name,
    icon: "",
    color: "melon",
    blobKey,
    mimeType,
    addedAt: Date.now(),
    addedBy,
  };
}
